import { motion } from "framer-motion";
import { usePageMeta } from "@/hooks/use-page-meta";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="mt-10">
    <h2 className="font-heading text-lg font-bold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#4B5563] md:text-base">
      {children}
    </div>
  </section>
);

export default function TermsOfUse() {
  usePageMeta(
    "Terms of Use — Bright Frontier Labs",
    "Terms of Use for Mu'min Kids and other apps published by Bright Frontier Labs, including optional auto-renewing Plus and Family subscriptions."
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="terms-of-use-page"
    >
      <div className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-3xl px-6 py-20 md:px-8 md:py-24">
          <span className="text-xs font-semibold uppercase tracking-widest text-white/50">
            Legal &mdash; 02
          </span>
          <h1
            data-testid="terms-heading"
            className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Terms of Use
          </h1>
          <p className="mt-4 text-sm text-white/50">
            Mu&rsquo;min Kids &middot; Last updated: September 21, 2026
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 md:py-20">
        <Section title="1. Acceptance of Terms" testId="terms-acceptance-section">
          <p>
            These Terms of Use (&quot;Terms&quot;) govern your use of
            Mu&rsquo;min Kids and other mobile applications and websites
            published by Bright Frontier Labs (&quot;we&quot;, &quot;our&quot;,
            or &quot;us&quot;) (collectively, the &quot;Services&quot;). By
            downloading, accessing, or using the Services, you agree to these
            Terms. If you do not agree, please do not use the Services.
          </p>
        </Section>

        <Section title="2. The Service" testId="terms-service-section">
          <p>
            Mu&rsquo;min Kids is an Islamic learning app for children ages
            3&ndash;10 and their families. Core learning content is free and
            works as a guest &mdash; no child account is required. Optional
            Plus and Family subscriptions unlock additional features and are
            available only to parents through parent-gated flows.
          </p>
        </Section>

        <Section title="3. Subscriptions and Billing" testId="terms-subscriptions-section">
          <p>
            Optional Plus and Family subscriptions are offered as
            auto-renewing subscriptions:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Prices and billing periods are shown by the store (Google Play or
              the Apple App Store) before you confirm a purchase;
            </li>
            <li>
              Payment is handled entirely by the platform store &mdash; we never
              see or store your card or bank details;
            </li>
            <li>
              Subscriptions renew automatically unless cancelled at least 24
              hours before the end of the current billing period;
            </li>
            <li>
              Cancellation is managed in your own store account settings (Google
              Play or Apple ID subscriptions). Cancelling stops future renewals;
              access continues until the end of the period you have already paid
              for;
            </li>
            <li>
              Refunds are handled according to the store&rsquo;s policies and
              applicable local law;
            </li>
            <li>
              A Restore Purchases option is available in the app to reinstate
              eligible purchases on a new or reset device signed in with the
              same store account.
            </li>
          </ul>
          <p>
            Local child progress &mdash; such as rewards, streaks, and completed
            lessons &mdash; is stored on the device and does not sync through a
            subscription or between devices.
          </p>
        </Section>

        <Section title="4. Children and Parental Supervision" testId="terms-supervision-section">
          <p>
            Mu&rsquo;min Kids is designed for children to use together with
            their families. Parents and guardians are responsible for
            supervising their children&rsquo;s use of the app, including any
            purchases. Account and subscription features are gated behind
            parent-only flows.
          </p>
        </Section>

        <Section title="5. Educational and Religious Content" testId="terms-content-section">
          <p>
            The app&rsquo;s lessons &mdash; including Arabic letters and words,
            Quran and Hifz practice, duas, good manners, Wudu, Salah, and
            Prophet stories &mdash; are prepared with care for young learners.
            They are educational aids and are not a substitute for qualified
            parental, teaching, or scholarly guidance. Please consult qualified
            teachers or scholars for religious rulings and detailed instruction.
          </p>
        </Section>

        <Section title="6. Acceptable Use" testId="terms-use-section">
          <p>You agree not to:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Copy, modify, distribute, sell, or lease any part of the Services;
            </li>
            <li>
              Reverse engineer or attempt to extract the source code of our
              apps, except where such restriction is prohibited by law;
            </li>
            <li>
              Use the Services in any way that violates applicable laws or
              regulations;
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Services;
            </li>
            <li>
              Attempt to gain unauthorized access to the Services or related
              systems.
            </li>
          </ul>
        </Section>

        <Section title="7. Intellectual Property" testId="terms-ip-section">
          <p>
            The Services, including all content, features, functionality,
            artwork, trademarks, logos, and software, are owned by Bright
            Frontier Labs or its licensors and are protected by copyright,
            trademark, and other intellectual property laws. Nothing in these
            Terms grants you any right to use our name, trademarks, or branding
            without our prior written consent.
          </p>
        </Section>

        <Section title="8. Third-Party Services" testId="terms-third-party-section">
          <p>
            The app relies on platform and service providers &mdash; including
            Google Play, the Apple App Store, Firebase Authentication, Google
            Sign-In, RevenueCat, and everyayah.com for recitation audio. Your
            use of those services is subject to their own terms and policies.
          </p>
        </Section>

        <Section title="9. Disclaimers" testId="terms-disclaimer-section">
          <p>
            The Services are provided on an &quot;as is&quot; and &quot;as
            available&quot; basis without warranties of any kind, whether
            express or implied, including implied warranties of
            merchantability, fitness for a particular purpose, and
            non-infringement. We do not warrant that the Services will be
            uninterrupted or error-free.
          </p>
        </Section>

        <Section title="10. Limitation of Liability" testId="terms-liability-section">
          <p>
            To the fullest extent permitted by law, Bright Frontier Labs shall
            not be liable for any indirect, incidental, special, consequential,
            or punitive damages, or any loss of profits, data, or goodwill,
            arising out of or related to your use of or inability to use the
            Services.
          </p>
        </Section>

        <Section title="11. Changes to These Terms" testId="terms-changes-section">
          <p>
            We may revise these Terms from time to time. The most current
            version will always be posted on this page with an updated
            &quot;Last updated&quot; date. Continued use of the Services after
            changes take effect means you accept the revised Terms.
          </p>
        </Section>

        <Section title="12. Governing Law" testId="terms-law-section">
          <p>
            These Terms are governed by the laws of the jurisdiction in which
            Bright Frontier Labs operates, without regard to conflict of law
            provisions. Mandatory consumer protections in your own jurisdiction
            remain unaffected.
          </p>
        </Section>

        <Section title="13. Contact Us" testId="terms-contact-section">
          <p>
            Questions about these Terms? Email us at{" "}
            <a
              href="mailto:support@brightfrontierlabs.com"
              data-testid="terms-mailto-link"
              className="font-medium text-[#0A0A0A] underline decoration-black/30 underline-offset-4 transition-colors duration-200 hover:decoration-black"
            >
              support@brightfrontierlabs.com
            </a>
            .
          </p>
        </Section>
      </div>
    </motion.div>
  );
}
